import { useState, useEffect } from 'react';

import { Button, Input, Select, Form, Alert } from 'antd';
import moment from 'moment';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { ClickEvents, UserRole } from 'utils/enums';
import { sortByIPA, getDisplayCompanyId, addCustomAppInsightsEvent } from 'utils/helpers';

import { Ipa } from 'store/app/ipa/actions';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import SearchMemberFormProps from './Proptypes';

import styles from './SearchMemberForm.module.scss';

const { Option } = Select;

const SearchMemberForm = (props: SearchMemberFormProps) => {

  const [ipaList, setIpaList] = useState([] as Ipa[]);
  const [provider, setProvider] = useState('');
  const [birth, setBirth] = useState('');
  const [showHelpfulText, setShowHelpfulText] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [form] = Form.useForm();

  const pcpOther = [
    {
      RmiCompanyFullName: "By PCP",
      RmiCompanyId: "PCP",
    },
    {
      RmiCompanyFullName: "By Other",
      RmiCompanyId: "OTHER"
    }
  ];

  useEffect(() => {
    getAllIPAsDesc();
  }, []);

  const getAllIPAsDesc = () => {


    return props.getAllDesc()
      .then((allIpa: any[]) => {
        if (sessionStorage.user_role === UserRole.IPA) {
          props.getContractedIPA(sessionStorage.user_id)
            .then((contractedIpa: string[]) => {
              const sortedIPA = sortByIPA(allIpa.filter(ipa => { return contractedIpa.includes(ipa.RmiCompanyId) }), 'RmiCompanyId');
              setIpaList(sortedIPA);
              setProvider(sortedIPA?.length > 0 ? sortedIPA[0].RmiCompanyId : "");
            });
        }

        else if (sessionStorage.user_role === UserRole.CASE) {
          const sortedIPA = sortByIPA(allIpa, 'RmiCompanyId');
          setIpaList(sortedIPA.filter(x => x.RmiCompanyId !== 'GAMC' && x.RmiCompanyId !== 'WMMC')); // temp fix to remove unsupported IPAs
          setProvider(sortedIPA?.length > 0 ? sortedIPA[0].RmiCompanyId : "");
        }

        else {
          setIpaList(pcpOther);
          setProvider(pcpOther[0].RmiCompanyId);
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const resetFields = () => {
    if (sessionStorage.user_role === UserRole.CASE || sessionStorage.user_role === UserRole.IPA) {
      setProvider(ipaList[0].RmiCompanyId);
    }
    else {
      setProvider(pcpOther[0].RmiCompanyId); //resetting to the default value
    }
    form.resetFields()
    setShowHelpfulText(false);
    setNoResults(false);
    setBirth('');
    if (!props.redirect) return props.newTableDataHandler([], false); // only resets the table on the search eligibility page
  }

  const onSearch = () => {

    form.validateFields().then((values: any) => {
      const form_data = {
        "MemberId": values.memberId,
        "Provider": provider,
        "FirstName": values.firstName,
        "LastName": values.lastName,
        "DateOfBirth": birth === '' ? null : birth,
        "UserId": sessionStorage.user_id,
        "CompanyId": sessionStorage.getItem("ipaGroup")
      };

      //This is to run the search on the Search Eligibility page after redirecting from Quality Overview page
      if (props.redirect) {
        return props.newTableDataHandler([], false, form_data);
      }

      addCustomAppInsightsEvent(ClickEvents.MEMBER_SEARCH, form_data);

      return props.search(form_data)
        .then((response: any) => {
          props.newTableDataHandler(response, true);
          setNoResults(response.length === 0);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    });
  };

  const allEmptyFields = () => {
    return !form.getFieldValue('memberId') && !form.getFieldValue('birth') && !form.getFieldValue('lastName') && !form.getFieldValue('firstName');
  }

  const pcpValidation = async (fieldData: any, value: any) => {
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;


    if (charLessThanFive(value)) {
      return Promise.reject('Please enter at least 5 characters.');
    }

    if (allEmptyFields()) {
      setShowHelpfulText(true);
      return Promise.reject('Required');
    }

    if (fieldData.field === 'memberId') {
      if (form.getFieldValue('memberId')) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }
    }

    if (fieldData.field === 'firstName') {
      const firstNameResolved = () => {
        return form.getFieldValue('firstName') && form.getFieldValue('lastName');
      }

      const missingFirstName = () => {
        return !form.getFieldValue('firstName') && form.getFieldValue('lastName');
      }

      if (firstNameResolved()) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }

      if (missingFirstName()) {
        setShowHelpfulText(false);
        return Promise.reject('Required');
      }
    }

    if (fieldData.field === 'lastName') {

      const lastNameResolved = () => {
        return form.getFieldValue('firstName') && form.getFieldValue('lastName');
      }

      const missingLastName = () => {
        return form.getFieldValue('firstName') && !form.getFieldValue('lastName');
      }

      if (lastNameResolved()) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }

      if (missingLastName()) {
        setShowHelpfulText(false);
        return Promise.reject('Required');
      }
    }

    if (fieldData.field === 'birth') {
      if (form.getFieldValue('birth')) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }
    }
  }

  const otherValidation = async (fieldData: any, value: any) => {
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    if (charLessThanFive(value)) {
      return Promise.reject('Please enter at least 5 characters.');
    }

    if (allEmptyFields()) {
      setShowHelpfulText(true);
      return Promise.reject('Required');
    }

    if (fieldData.field === 'memberId') {
      const missingMemberIdAndFirstLastNames = () => {
        return !form.getFieldValue('memberId') && form.getFieldValue('birth') && !(form.getFieldValue('firstName') || form.getFieldValue('lastName'));
      }

      if (missingMemberIdAndFirstLastNames()) {
        setShowHelpfulText(false);
        return Promise.reject('Required');
      }
    }

    if (fieldData.field === 'firstName') {

      if (allEmptyFields()) {
        setShowHelpfulText(true);
        return Promise.reject('Required');
      }

      if ((form.getFieldValue('firstName') && form.getFieldValue('lastName')) || (form.getFieldValue('birth') && !(form.getFieldValue('firstName') || form.getFieldValue('lastName')))) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }

      if ((!form.getFieldValue('firstName') || form.getFieldValue('lastName')) && !form.getFieldValue('memberId')) {
        setShowHelpfulText(false);
        return Promise.reject('Required');
      }
    }

    if (fieldData.field === 'lastName') {

      const lastNameResolved = () => {
        return (form.getFieldValue('firstName') && form.getFieldValue('lastName')) || (form.getFieldValue('birth') && !(form.getFieldValue('firstName') || form.getFieldValue('lastName')));
      }

      const missingLastNameOrMemberId = () => {
        return (form.getFieldValue('firstName') || !form.getFieldValue('lastName')) && !form.getFieldValue('memberId');
      }

      if (allEmptyFields()) {
        setShowHelpfulText(true);
        return Promise.reject('Required');
      }

      else if (lastNameResolved()) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }

      else if (missingLastNameOrMemberId()) {
        return Promise.reject('Required');
      }
    }

    if (fieldData.field === 'birth') {
      const atLeastOne = () => {
        return form.getFieldValue('memberId') || form.getFieldValue('firstName') || form.getFieldValue('lastName');
      }

      if (allEmptyFields()) {
        setShowHelpfulText(true);
        return Promise.reject('Required');
      }

      else if (form.getFieldValue('birth')) {
        setShowHelpfulText(false);
        return Promise.resolve();
      }

      else if (atLeastOne()) {
        setShowHelpfulText(false);
        return Promise.reject('Required');
      }
    }
  }

  const updateProvider = (provider: string) => {
    resetFields();
    setNoResults(false);
    setProvider(provider);
  }

  return (
    <>
      <Form
        form={form}
        name="member-search-form"
        onFinish={onSearch}
      >
        <div className={`${styles['member-search-form']} flex-lg-wrap justify-content-start mb-3 ml-0 pb-3`}>
          <div className="row">
            <div className="col-lg-8">
              <FloatLabel label="Provider:" value={provider}>
              <Select
                showSearch
                value={provider}
                placeholder="Select a Provider..."
                onChange={value => updateProvider(value)}
              >
                {ipaList.map((ipa: any, key: number) => {
                  return <Option key={key} value={ipa.RmiCompanyId}>{getDisplayCompanyId(ipa.RmiCompanyId)}</Option>
                })}
              </Select>
              </FloatLabel>
            </div>

            <div className="col-lg-8">
              <FloatLabel
                name="memberId"
                label="Member ID:"
                form={form}
                validateTrigger={['onSubmit']}
                rules={[
                  () => ({
                    validator: provider === 'PCP' || sessionStorage.user_role === UserRole.CASE ? pcpValidation : otherValidation
                  })
                ]}
              >
                <Input />
              </FloatLabel>
            </div>

            <div className="col-lg-8">
              <FloatLabel
                name="birth"
                label="DOB (MM/DD/YYYY)"
                form={form}
                validateTrigger={['onSubmit']}
                rules={[
                  () => ({
                    validator: provider === 'PCP' || sessionStorage.user_role === UserRole.CASE ? pcpValidation : otherValidation
                  }),
                  () => ({
                    async validator() {
                      const validBirthDate = () => (!birth || (moment(birth).isValid() && moment(birth).year() >= 1900)) && (!birth || birth.match(/^\d{2}\/\d{2}\/\d{4}$/));

                      if (validBirthDate()) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error('Invalid Date'));
                    }
                  })
                ]}
              >
                <DatePickerLocalised
                  date={birth}
                  setDate={setBirth}
                  formInstance={form}
                  formItemName={'birth'}
                />
              </FloatLabel>
            </div>

            <div className="col-lg-8">
              <FloatLabel
                name="lastName"
                label="Last Name"
                form={form}
                validateTrigger={['onSubmit']}
                rules={[
                  () => ({
                    validator: provider === 'PCP' || sessionStorage.user_role === UserRole.CASE ? pcpValidation : otherValidation
                  }),
                  () => ({
                    async validator(_: any, value: any) {
                      const charLessThanTwo = (value: any) => value && value.length < 2;

                      if (charLessThanTwo(value?.trim())) {
                        return Promise.reject('Please enter at least 2 characters.');
                      }
                    }
                  })
                ]}
              >
                <Input />
              </FloatLabel>
            </div>

            <div className="col-lg-8">
              <FloatLabel
                  name="firstName"
                  label="First Name"
                  form={form}
                  validateTrigger={['onSubmit']}
                  rules={[
                    () => ({
                      validator: provider === 'PCP' || sessionStorage.user_role === UserRole.CASE ? pcpValidation : otherValidation,
                    }),
                    () => ({
                      async validator(_: any, value: any) {
                        const charLessThanTwo = (value: any) => value && value.length < 2;

                        if (charLessThanTwo(value?.trim())) {
                          return Promise.reject('Please enter at least 2 characters.');
                        }
                      }
                    })
                  ]}
                >
                  <Input />
              </FloatLabel>
            </div>

            <div className="btn-wrapper col-lg-8">
              <Button type="primary" style={{ width: '100%' }} htmlType={'submit'} loading={props.isSearchingMembers}>SEARCH</Button>
              <Button style={{ width: '100%' }} onClick={resetFields}>RESET</Button>
            </div>
          </div>

          {showHelpfulText && (
            <div className="row">
              { (provider === 'PCP' || sessionStorage.user_role === UserRole.CASE) ?
                (<Alert
                  message={
                    <>
                      Please meet one of the following requirements: <br />
                      MemberID ONLY <br />
                      DOB (mm/dd/yyyy) ONLY <br />
                      Last Name + First Name ONLY
                    </>
                  }
                  type="error"
                  className="col-lg-24"
                />
              ) : null}

              { (sessionStorage.user_role !== UserRole.CASE && provider === 'OTHER') ?
                (<Alert

                  message={
                    <>
                      Please meet one of the following requirements:<br />
                      MemberID + DOB (mm/dd/yyyy) <br />
                      Last Name + First Name + DOB (mm/dd/yyyy)
                    </>
                  }
                  type="error"
                  className="col-lg-24"
                />
              ) : null}

            </div>
          )}

          { (noResults && !showHelpfulText) ? (
            <Alert
              className="col-lg-24"
              style={{ textAlign: 'center' }}
              message={<>There were no results for this search. Please check the fields and try again.</>}
              type="error"
            />
          ) : null}
        </div>
      </Form>
    </>
  );
}

export default SearchMemberForm;